/** Triage-overzicht: taken die metadata missen, per type — klik opent Organize Inbox. */
import { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { StatBlock } from '@/components/ui/StatBlock';
import { useTasksContext } from '@/features/tasks';
import {
  getDurationTriageTasks,
  getUntaggedContextTasks,
  getUntaggedProjectTasks,
} from '@/features/organize/utils/inboxTriageUtils';
import { ONBOARDING_HINT_TRIAGE } from '@/constants/onboardingHints';
import { ONBOARDING_HINT_QUEUE_PRIORITIES } from '@/features/onboarding/constants/onboardingHintQueue';
import { useRegisterOnboardingQueuedHint } from '@/features/onboarding/context/useRegisterOnboardingQueuedHint';
import { getOnboardingHighlightSx } from '@/features/onboarding/utils/onboardingHighlightSx';
import { useTriageHeroHint } from '@/features/today/hooks/use-triage-hero-hint';
import { useT } from '@/i18n/useT';

/**
 * @param {Object} props
 * @param {string | undefined} props.userId
 */
const TriageHeroCard = ({ userId }) => {
  const t = useT();
  const navigate = useNavigate();
  const { allTasks } = useTasksContext();

  const rows = useMemo(() => {
    const source = allTasks || [];
    return [
      {
        key: 'time',
        label: t('today.triage.time', 'Standaardduur'),
        value: getDurationTriageTasks(source).length,
        color: 'primary.main',
      },
      {
        key: 'context',
        label: t('today.triage.context', 'Context'),
        value: getUntaggedContextTasks(source).length,
        color: 'secondary.main',
      },
      {
        key: 'project',
        label: t('today.triage.project', 'Project'),
        value: getUntaggedProjectTasks(source).length,
        color: 'error.main',
      },
    ].filter(({ value }) => value > 0);
  }, [allTasks, t]);

  const cardVisible = rows.length > 0;
  const { showHint, dismissHint } = useTriageHeroHint({
    cardVisible,
    userId,
  });
  const triageHintTitle = t(
    'onboarding.triageHint.title',
    'Sommige taken missen nog details.',
  );
  const triageHintBody = t(
    'onboarding.triageHint.body',
    'Klik op dit blok om ze in de Inbox aan te vullen.',
  );
  const { isActive: triageHintActive } = useRegisterOnboardingQueuedHint({
    id: ONBOARDING_HINT_TRIAGE,
    priority: ONBOARDING_HINT_QUEUE_PRIORITIES[ONBOARDING_HINT_TRIAGE],
    eligible: cardVisible,
    pending: showHint,
    title: triageHintTitle,
    body: triageHintBody,
    testId: 'triage-hero-hint',
    onDismiss: dismissHint,
  });

  if (!cardVisible) return null;

  const handleClick = () => {
    if (showHint) {
      dismissHint();
    }
    navigate('/app/organize?tab=inbox');
  };

  return (
    <Box sx={getOnboardingHighlightSx(triageHintActive)}>
      <StatBlock
        data-testid="triage-hero-card"
        onClick={handleClick}
      >
        <Box sx={{ textAlign: 'left', width: '100%' }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              mb: 1,
            }}
          >
            {t('today.triage', 'Inbox')}
          </Typography>
          {rows.map(({ key, label, value, color }, index) => (
            <Box key={key}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: '2ch',
                    textAlign: 'right',
                  }}
                >
                  {value}
                </Typography>
              </Box>
              {index < rows.length - 1 ? <Divider /> : null}
            </Box>
          ))}
        </Box>
      </StatBlock>
    </Box>
  );
};

export default TriageHeroCard;
